<template>
  <div class="employee-titles-settings">
    <v-container fluid>
      <!-- Page Header -->
      <div class="d-flex justify-space-between align-center mb-6">
        <div>
          <h1 class="text-h4 font-weight-bold mb-2">المسميات الوظيفية</h1>
          <p class="text-body-1 text-grey">إدارة المسميات والألقاب الوظيفية للموظفين</p>
        </div>
      </div>

      <!-- Content Card -->
      <v-card>
        <v-card-text class="pa-6">
          <div class="d-flex justify-space-between align-center mb-4">
            <h2 class="text-h5">قائمة المسميات الوظيفية</h2>
            <v-btn
            v-if="hasPermission('admin.employee-titles.store')"
              v-action="{ module: 'employee-titles', action: 'store' }"
              color="primary"
              prepend-icon="mdi-plus"
              @click="openAddEmployeeTitleDialog"
            >
              إضافة مسمى جديد
            </v-btn>
          </div>

          <v-data-table
            :headers="employeeTitleHeaders"
            :items="employeeTitles"
            :search="employeeTitleSearch"
            :loading="loading"
            color="primary"
            first-icon="mdi-chevron-left"
            last-icon="mdi-chevron-right"
            next-icon="mdi-chevron-right"
            prev-icon="mdi-chevron-left"
            show-current-page
            show-first-last-page
            show-first-page
            show-last-page
            show-next-page
            show-previous-page
            class="elevation-1"
          >
            <template #top>
              <v-text-field
                v-model="employeeTitleSearch"
                prepend-inner-icon="mdi-magnify"
                label="بحث في المسميات الوظيفية"
                variant="outlined"
                density="compact"
                class="mx-4 mt-4"
              ></v-text-field>
            </template>

            <template v-slot:[`item.short_name`]="{ item }">
              <v-chip color="blue" size="small" variant="outlined">
                {{ item.short_name }}
              </v-chip>
            </template>

        

            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
                v-if="hasPermission('admin.employee-titles.update')"
                v-action="{ module: 'employee-titles', action: 'update' }"
                icon
                size="small"
                color="primary"
                variant="text"
                @click="editEmployeeTitle(item)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                v-if="hasPermission('admin.employee-titles.destroy')"
                v-action="{ module: 'employee-titles', action: 'destroy' }"
                icon
                size="small"
                color="error"
                variant="text"
                @click="confirmDelete(item, 'employeeTitle')"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Employee Title Dialog -->
    <v-dialog v-model="showEmployeeTitleDialog" max-width="500px">
      <v-card>
        <v-card-title>
          {{ editingEmployeeTitle ? 'تعديل المسمى الوظيفي' : 'إضافة مسمى وظيفي جديد' }}
        </v-card-title>
        <v-card-text>
          <v-form ref="employeeTitleForm" v-model="valid" @submit.prevent="saveEmployeeTitle">
            <v-text-field
              v-model="employeeTitleForm.name"
              label="المسمى الوظيفي"
              variant="outlined"
              required
              :rules="[v => !!v || 'المسمى الوظيفي مطلوب']"
              class="mb-4"
              placeholder="مهندس، محاسب، طبيب..."
            ></v-text-field>
            
            <v-text-field
              v-model="employeeTitleForm.short_name"
              label="المسمى المختصر"
              variant="outlined"
              required
              :rules="[v => !!v || 'المسمى المختصر مطلوب']"
              class="mb-4"
              placeholder="م.، د.، أ...."
              hint="للاستخدام في التوقيعات والمراسلات"
              persistent-hint
            ></v-text-field>
          
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showEmployeeTitleDialog = false">إلغاء</v-btn>
          <v-btn color="primary" @click="saveEmployeeTitle">حفظ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h6">تأكيد الحذف</v-card-title>
        <v-card-text>
          هل أنت متأكد من حذف المسمى الوظيفي؟
          <br><strong>{{ itemToDelete?.name }} ({{ itemToDelete?.short_name }})</strong>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog = false">إلغاء</v-btn>
          <v-btn color="error" @click="confirmDeleteAction">حذف</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import employeeTitleService from '@/services/employeeTitleService'
import { hasPermission, hasModule } from '@/utils/permissions'  

export default {
  name: 'EmployeeTitlesView',
  data() {
    return {
      loading: false,
      valid: false,
      employeeTitleSearch: '',
      showEmployeeTitleDialog: false,
      editingEmployeeTitle: null,
      employeeTitles: [],
      employeeTitleForm: {
        name: '',
        short_name: '',
        desc: '',
        active: true
      },
      employeeTitleHeaders: [
        { title: 'المسمى الوظيفي', key: 'name', align: 'start' },
        { title: 'المسمى المختصر', key: 'short_name', align: 'center' },
       
        { title: 'الإجراءات', key: 'actions', align: 'center', sortable: false }
      ],
      deleteDialog: false,
      itemToDelete: null,
      deleteType: ''
    }
  },
  async mounted() {
    await this.loadEmployeeTitles()
  },
  methods: {
    hasPermission(permission) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasPermission(permission)
    },
    
    hasModule(moduleName) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasModule(moduleName)
    },
    async loadEmployeeTitles() {
      this.loading = true
      try {
        const response = await employeeTitleService.getEmployeeTitles()
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          this.employeeTitles = response.data.data
        } else if (response.data && Array.isArray(response.data)) {
          this.employeeTitles = response.data
        } else {
          this.employeeTitles = []
        }
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في جلب المسميات الوظيفية'
        })
      } finally {
        this.loading = false
      }
    },

    openAddEmployeeTitleDialog() {
      this.editingEmployeeTitle = null
      this.employeeTitleForm = {
        name: '',
        short_name: '',
        desc: '',
        active: true
      }
      this.showEmployeeTitleDialog = true
    },

    editEmployeeTitle(employeeTitle) {
      this.editingEmployeeTitle = employeeTitle
      this.employeeTitleForm = {
        name: employeeTitle.name,
        short_name: employeeTitle.short_name,
        desc: employeeTitle.desc,
        active: Boolean(employeeTitle.active)
      }
      this.showEmployeeTitleDialog = true
    },

    async saveEmployeeTitle() {
      if (!this.$refs.employeeTitleForm.validate()) return

      this.loading = true
      try {
        if (this.editingEmployeeTitle) {
          await employeeTitleService.updateEmployeeTitle(this.editingEmployeeTitle.id, this.employeeTitleForm)
          this.$store.dispatch('alerts/showAlert', {
            type: 'success',
            message: 'تم تحديث المسمى الوظيفي بنجاح'
          })
        } else {
          await employeeTitleService.createEmployeeTitle(this.employeeTitleForm)
          this.$store.dispatch('alerts/showAlert', {
            type: 'success',
            message: 'تم إضافة المسمى الوظيفي بنجاح'
          })
        }
        
        this.showEmployeeTitleDialog = false
        await this.loadEmployeeTitles()
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في حفظ المسمى الوظيفي'
        })
      } finally {
        this.loading = false
      }
    },

    confirmDelete(item, type) {
      this.itemToDelete = item
      this.deleteType = type
      this.deleteDialog = true
    },

    async confirmDeleteAction() {
      if (!this.itemToDelete) return

      this.loading = true
      try {
        await employeeTitleService.deleteEmployeeTitle(this.itemToDelete.id)
        this.$store.dispatch('alerts/showAlert', {
          type: 'success',
          message: 'تم حذف المسمى الوظيفي بنجاح'
        })
        
        this.deleteDialog = false
        await this.loadEmployeeTitles()
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في حذف المسمى الوظيفي'
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.employee-titles-settings {
  min-height: 100vh;
}
</style> 